import { z } from "zod";
import { widgetSpacingSchema, type WidgetSpacing } from "./widget-spacing.js";
export * from "./widget-spacing.js";

export * from "./menu.js";

export const idSchema = z.string().uuid();

export const clientStatusSchema = z.enum(["active", "suspended", "expired", "archived"]);
export type ClientStatus = z.infer<typeof clientStatusSchema>;

export const expiredTvPolicySchema = z.enum([
  "continue_cached",
  "show_expired_screen",
  "show_disabled_screen",
]);

export interface ClientSubscription {
  status: ClientStatus;
  activeFrom?: Date | null;
  activeUntil?: Date | null;
}

export function effectiveClientStatus(
  subscription: ClientSubscription,
  now = new Date(),
): ClientStatus {
  if (subscription.status === "archived" || subscription.status === "suspended") {
    return subscription.status;
  }
  if (subscription.activeFrom && subscription.activeFrom.getTime() > now.getTime()) {
    return "suspended";
  }
  if (subscription.activeUntil && subscription.activeUntil.getTime() <= now.getTime()) {
    return "expired";
  }
  return subscription.status === "expired" ? "expired" : "active";
}

export const fieldTypeSchema = z.enum([
  "text",
  "textarea",
  "richText",
  "html",
  "number",
  "boolean",
  "date",
  "time",
  "datetime",
  "color",
  "select",
  "multiSelect",
  "image",
  "logo",
  "video",
  "youtube",
  "youtubeList",
  "url",
  "phone",
  "whatsapp",
  "vcard",
  "rssSource",
  "tickerContent",
  "wordpressPosts",
  "imageCarousel",
  "textCarousel",
  "contentCarousel",
  "fontFamily",
  "fontSize",
  "alignment",
  "position",
]);
export type FieldType = z.infer<typeof fieldTypeSchema>;

export const youtubeListValueSchema = z
  .object({
    urls: z.array(z.string().max(2_000)).min(1).max(100),
    shuffle: z.boolean().default(false),
    controllerEnabled: z.boolean().default(false),
  })
  .strict();
export type YouTubeListValue = z.infer<typeof youtubeListValueSchema>;

export const youtubeValueSchema = z.union([
  z.string().max(2_000),
  z
    .object({
      url: z.string().max(2_000),
      playbackMode: z.enum(["loop", "once", "recommended"]).default("loop"),
      shuffle: z.boolean().default(false),
      controllerEnabled: z.boolean().default(false),
    })
    .strict(),
]);
export type YouTubeValue = z.infer<typeof youtubeValueSchema>;

export function youtubeUrl(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  const parsed = youtubeValueSchema.safeParse(value);
  return parsed.success && typeof parsed.data !== "string" ? parsed.data.url : undefined;
}

export const tickerContentValueSchema = z.discriminatedUnion("mode", [
  z.object({
    mode: z.literal("manual"),
    headlines: z.string().max(12_000),
  }),
  z.object({
    mode: z.literal("rss"),
    rssSourceId: idSchema,
    headlines: z.array(z.string().min(1).max(2_000)).max(1_000).optional(),
  }),
]);
export type TickerContentValue = z.infer<typeof tickerContentValueSchema>;

export const wordpressPostSchema = z
  .object({
    title: z.string().min(1).max(500),
    description: z.string().max(2_000),
    url: z
      .url()
      .max(2_000)
      .refine((value) => {
        try {
          const url = new URL(value);
          return ["http:", "https:"].includes(url.protocol) && !url.username && !url.password;
        } catch {
          return false;
        }
      }, "Post URLs must use credential-free HTTP(S)")
      .optional(),
  })
  .strict();
export const wordpressPostsValueSchema = z
  .object({
    mode: z.literal("wordpress"),
    apiUrl: z
      .url()
      .max(2_000)
      .refine((value) => {
        let url: URL;
        try {
          url = new URL(value);
        } catch {
          return false;
        }
        const host = url.hostname.toLowerCase().replace(/\.$/, "");
        return (
          url.protocol === "https:" &&
          (!url.port || url.port === "443") &&
          !url.username &&
          !url.password &&
          !url.search &&
          !url.hash &&
          !host.includes(":") &&
          !/^[0-9.]+$/.test(host) &&
          host.includes(".") &&
          host !== "localhost.localdomain" &&
          !/(?:^|\.)(?:localhost|local|internal|test|invalid)$/.test(host) &&
          host !== "metadata.google.internal" &&
          /\/wp-json(?:\/wp\/v2)?\/?$/.test(url.pathname)
        );
      }, "Use a credential-free HTTPS wp-json URL, optionally ending in /wp/v2"),
    postType: z
      .string()
      .regex(
        /^[a-z0-9][a-z0-9_-]{0,63}$/,
        "Use the REST post-type slug, for example posts or news",
      ),
    limit: z.number().int().min(1).max(100).default(10),
    posts: z.array(wordpressPostSchema).max(100).optional(),
  })
  .strict();
export type WordPressPostsValue = z.infer<typeof wordpressPostsValueSchema>;

export const vCardValueSchema = z
  .object({
    fullName: z.string().min(1).max(200),
    organization: z.string().max(200).default(""),
    title: z.string().max(200).default(""),
    phone: z
      .string()
      .regex(/^(?:\+?[0-9][0-9 ()-]{5,24})?$/, "Enter a valid international phone number")
      .default(""),
    email: z.union([z.literal(""), z.string().email().max(254)]).default(""),
    website: z
      .union([
        z.literal(""),
        z
          .url()
          .max(2_000)
          .refine((value) => value.startsWith("https://") || value.startsWith("http://")),
      ])
      .default(""),
  })
  .strict();
export type VCardValue = z.infer<typeof vCardValueSchema>;

export const slideImageAssetSchema = z
  .object({
    id: idSchema,
    url: z.string().max(2_000),
    mimeType: z.string().startsWith("image/").max(200),
  })
  .strict()
  .superRefine((asset, ctx) => {
    if (asset.url !== `/api/v1/media/assets/${asset.id}`) {
      ctx.addIssue({ code: "custom", path: ["url"], message: "Use a locally hosted image asset" });
    }
  });
export type SlideImageAsset = z.infer<typeof slideImageAssetSchema>;

export const imageCarouselValueSchema = z.array(slideImageAssetSchema).max(100);
export type ImageCarouselValue = z.infer<typeof imageCarouselValueSchema>;

export const textCarouselValueSchema = z
  .array(
    z
      .object({
        title: z.string().min(1).max(240),
        description: z.string().max(2_000).default(""),
      })
      .strict(),
  )
  .max(100);
export type TextCarouselValue = z.infer<typeof textCarouselValueSchema>;

export const contentCarouselValueSchema = z
  .array(
    z
      .object({
        image: slideImageAssetSchema,
        title: z.string().min(1).max(240),
        description: z.string().max(2_000).default(""),
      })
      .strict(),
  )
  .max(100);
export type ContentCarouselValue = z.infer<typeof contentCarouselValueSchema>;

const fieldOptionSchema = z.object({
  label: z.string().min(1).max(120),
  value: z.union([z.string(), z.number(), z.boolean()]),
});

export const fieldDefinitionSchema = z
  .object({
    key: z.string().regex(/^[a-z][A-Za-z0-9_]*$/),
    label: z.string().min(1).max(120),
    type: fieldTypeSchema,
    required: z.boolean().default(false),
    defaultValue: z.unknown().optional(),
    previewText: z.string().max(2_000).optional(),
    placeholder: z.string().max(240).optional(),
    helpText: z.string().max(500).optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    minLength: z.number().int().nonnegative().optional(),
    maxLength: z.number().int().positive().optional(),
    pattern: z.string().max(240).optional(),
    accept: z.array(z.string()).max(12).optional(),
    maxFileSizeMb: z.number().positive().max(500).optional(),
    options: z.array(fieldOptionSchema).max(100).optional(),
    visibleWhen: z
      .object({
        field: z.string(),
        equals: z.union([z.string(), z.number(), z.boolean()]),
      })
      .optional(),
    localization: z.boolean().default(false),
    allowedClientOverrides: z.array(z.string()).max(20).default([]),
    adminOnly: z.boolean().optional(),
  })
  .strict();
export type FieldDefinition = z.infer<typeof fieldDefinitionSchema>;

export const layoutNodeTypeSchema = z.enum([
  "container",
  "stack",
  "grid",
  "text",
  "label",
  "verticalLabel",
  "html",
  "image",
  "video",
  "youtube",
  "youtubeList",
  "logo",
  "ticker",
  "overlay",
  "shape",
  "square",
  "circle",
  "line",
  "spacer",
  "dateTime",
  "digitalTime",
  "displayDate",
  "analogClock",
  "qrCode",
  "staticImage",
  "wordpressPostQr",
  "qrCall",
  "qrUrl",
  "qrWhatsApp",
  "qrVCard",
  "wordpressPosts",
  "imageCarousel",
  "textCarousel",
  "contentCarousel",
]);
export type LayoutNodeType = z.infer<typeof layoutNodeTypeSchema>;

export const slidePurposeSchema = z.enum(["slide", "overlay"]);
export type SlidePurpose = z.infer<typeof slidePurposeSchema>;

export const allowedStyleProperties = [
  "alignItems",
  "alignSelf",
  "background",
  "backgroundColor",
  "border",
  "borderColor",
  "borderRadius",
  "borderStyle",
  "borderWidth",
  "bottom",
  "boxSizing",
  "color",
  "display",
  "flex",
  "flexDirection",
  "flexWrap",
  "fontFamily",
  "fontSize",
  "fontStyle",
  "fontWeight",
  "gap",
  "gridTemplateColumns",
  "height",
  "inset",
  "justifyContent",
  "left",
  "letterSpacing",
  "lineHeight",
  "margin",
  "marginBottom",
  "marginLeft",
  "marginRight",
  "marginTop",
  "maxHeight",
  "maxWidth",
  "minHeight",
  "minWidth",
  "objectFit",
  "objectPosition",
  "opacity",
  "overflow",
  "padding",
  "paddingBottom",
  "paddingLeft",
  "paddingRight",
  "paddingTop",
  "position",
  "right",
  "textAlign",
  "textDecoration",
  "textShadow",
  "textTransform",
  "top",
  "transform",
  "whiteSpace",
  "width",
  "zIndex",
] as const;

const safeStyleValueSchema = z
  .union([z.string().max(500), z.number()])
  .superRefine((value, ctx) => {
    if (typeof value !== "string") return;
    const normalized = value.toLowerCase().replace(/\s/g, "");
    if (
      normalized.includes("javascript:") ||
      normalized.includes("expression(") ||
      normalized.includes("@import") ||
      normalized.includes("</") ||
      normalized.includes("url(")
    ) {
      ctx.addIssue({ code: "custom", message: "Unsafe style value" });
    }
  });

export const safeStyleSchema = z
  .partialRecord(z.enum(allowedStyleProperties), safeStyleValueSchema)
  .default({});

export const entranceAnimationSchema = z.enum([
  "none",
  "fade-in",
  "blur-in",
  "slide-in-left",
  "slide-in-right",
  "slide-in-up",
  "slide-in-down",
  "zoom-in-subtle",
  "zoom-in",
  "bounce-in",
  "rotate-in",
  "flip-in",
  "reveal-up",
]);
export const exitAnimationSchema = z.enum([
  "none",
  "fade-out",
  "slide-out-left",
  "slide-out-right",
  "slide-out-up",
  "slide-out-down",
  "zoom-out-subtle",
]);
export type EntranceAnimation = z.infer<typeof entranceAnimationSchema>;
export type ExitAnimation = z.infer<typeof exitAnimationSchema>;

const carouselColorSchema = z.union([
  z.string().regex(/^#[0-9a-fA-F]{6}$/),
  z.literal("transparent"),
]);
const carouselTextStyleSchema = z
  .object({
    color: carouselColorSchema,
    fontFamily: z.string().min(1).max(200),
    fontSize: z.number().int().min(8).max(300),
    fontWeight: z.number().int().min(100).max(900),
    fontStyle: z.enum(["normal", "italic"]),
    textDecoration: z.enum(["none", "underline", "line-through"]),
    textTransform: z.enum(["none", "uppercase", "lowercase", "capitalize"]),
    letterSpacing: z.number().min(-10).max(50),
    lineHeight: z.number().min(0.5).max(4),
    textAlign: z.enum(["left", "center", "right"]),
  })
  .strict();
export const carouselSettingsSchema = z
  .object({
    autoplay: z.boolean(),
    intervalMs: z.number().int().min(1_000).max(60_000),
    transitionDurationMs: z.number().int().min(100).max(5_000),
    transition: z.enum(["slide", "fade", "zoom", "flip", "blur", "rotate", "bounce"]),
    direction: z.enum(["left", "right", "up", "down"]),
    loop: z.boolean(),
    showIndicators: z.boolean(),
    indicatorPosition: z.enum(["top", "bottom"]),
    indicatorStyle: z.enum(["dots", "bars"]),
    indicatorColor: carouselColorSchema,
    activeIndicatorColor: carouselColorSchema,
    indicatorSize: z.number().int().min(3).max(40),
    indicatorGap: z.number().int().min(0).max(60),
    showArrows: z.boolean(),
    showExcerpt: z.boolean().optional(),
    arrowColor: carouselColorSchema,
    arrowBackgroundColor: carouselColorSchema,
    arrowSize: z.number().int().min(12).max(120),
    cardBackgroundColor: carouselColorSchema,
    cardBorderColor: carouselColorSchema,
    cardBorderWidth: z.number().int().min(0).max(30),
    cardBorderRadius: z.number().int().min(0).max(300),
    cardPadding: z.number().int().min(0).max(200),
    imageFit: z.enum(["cover", "contain", "fill"]),
    imagePosition: z.enum([
      "center",
      "top",
      "bottom",
      "left",
      "right",
      "top left",
      "top right",
      "bottom left",
      "bottom right",
    ]),
    contentLayout: z.enum(["top", "bottom", "left", "right", "background"]),
    imageAreaPercent: z.number().int().min(20).max(80),
    contentAlign: z.enum(["start", "center", "end"]),
    textGap: z.number().int().min(0).max(100),
    title: carouselTextStyleSchema,
    description: carouselTextStyleSchema,
  })
  .strict();
export type CarouselSettings = z.infer<typeof carouselSettingsSchema>;
export const defaultCarouselSettings: CarouselSettings = {
  autoplay: true,
  intervalMs: 5_000,
  transitionDurationMs: 600,
  transition: "slide",
  direction: "left",
  loop: true,
  showIndicators: true,
  indicatorPosition: "bottom",
  indicatorStyle: "dots",
  indicatorColor: "#94a3b8",
  activeIndicatorColor: "#ffffff",
  indicatorSize: 10,
  indicatorGap: 8,
  showArrows: false,
  showExcerpt: true,
  arrowColor: "#ffffff",
  arrowBackgroundColor: "#111827",
  arrowSize: 40,
  cardBackgroundColor: "transparent",
  cardBorderColor: "#ffffff",
  cardBorderWidth: 0,
  cardBorderRadius: 20,
  cardPadding: 20,
  imageFit: "cover",
  imagePosition: "center",
  contentLayout: "top",
  imageAreaPercent: 60,
  contentAlign: "start",
  textGap: 12,
  title: {
    color: "#ffffff",
    fontFamily: "Inter, sans-serif",
    fontSize: 48,
    fontWeight: 700,
    fontStyle: "normal",
    textDecoration: "none",
    textTransform: "none",
    letterSpacing: 0,
    lineHeight: 1.1,
    textAlign: "left",
  },
  description: {
    color: "#e2e8f0",
    fontFamily: "Inter, sans-serif",
    fontSize: 28,
    fontWeight: 400,
    fontStyle: "normal",
    textDecoration: "none",
    textTransform: "none",
    letterSpacing: 0,
    lineHeight: 1.35,
    textAlign: "left",
  },
};

export interface LayoutNode {
  spacing?: WidgetSpacing | undefined;
  id?: string | undefined;
  type: LayoutNodeType;
  binding?: string | undefined;
  wordpressSourceBinding?: string | undefined;
  staticImageAssetId?: string | undefined;
  verticalLabelOrientation?: "clockwise" | "counterclockwise" | "upright" | undefined;
  variant?: "title" | "description" | "body" | "caption" | undefined;
  styles?: Partial<Record<(typeof allowedStyleProperties)[number], string | number>> | undefined;
  responsive?:
    | {
        portrait?:
          Partial<Record<(typeof allowedStyleProperties)[number], string | number>> | undefined;
        landscape?:
          Partial<Record<(typeof allowedStyleProperties)[number], string | number>> | undefined;
      }
    | undefined;
  animation?: EntranceAnimation | ExitAnimation | undefined;
  animationDurationMs?: number | undefined;
  animationDelayMs?: number | undefined;
  animationIteration?: "once" | "infinite" | undefined;
  textFit?: "fixed" | "shrink" | undefined;
  textWrap?: "wrap" | "nowrap" | "preserve" | undefined;
  minFontSize?: number | undefined;
  ticker?:
    | {
        direction: "left" | "right";
        speed: number;
        separator: string;
        separatorColor: string;
        separatorSpacing: number;
        sequenceGap: number;
      }
    | undefined;
  digitalClock?:
    | {
        showSeconds: boolean;
        use24Hour: boolean;
      }
    | undefined;
  dateFormat?: string | undefined;
  analogClock?:
    | {
        showHourMarkers: boolean;
        showSecondHand: boolean;
        showNumbers?: boolean | undefined;
        numberColor?: string | undefined;
        numberFontFamily?: string | undefined;
        numberFontSize?: number | undefined;
        numberFontWeight?: number | undefined;
        numberRadius?: number | undefined;
        markerColor: string;
        hourHandColor: string;
        minuteHandColor: string;
        secondHandColor: string;
        markerLength: number;
        hourHandLength: number;
        minuteHandLength: number;
        secondHandLength: number;
        handThickness: number;
      }
    | undefined;
  carousel?: CarouselSettings | undefined;
  positioning?:
    | {
        mode: "free" | "center";
        horizontalOffset: { value: number; unit: "%" | "px" };
        verticalOffset: { value: number; unit: "%" | "px" };
      }
    | undefined;
  visibleWhen?: { field: string; equals: string | number | boolean } | undefined;
  children?: LayoutNode[] | undefined;
}

export const layoutNodeSchema: z.ZodType<LayoutNode> = z.lazy(() =>
  z
    .object({
      id: z.string().max(80).optional(),
      type: layoutNodeTypeSchema,
      spacing: widgetSpacingSchema.optional(),
      binding: z.string().max(80).optional(),
      wordpressSourceBinding: z.string().min(1).max(80).optional(),
      staticImageAssetId: idSchema.optional(),
      verticalLabelOrientation: z.enum(["clockwise", "counterclockwise", "upright"]).optional(),
      variant: z.enum(["title", "description", "body", "caption"]).optional(),
      styles: safeStyleSchema.optional(),
      responsive: z
        .object({
          portrait: safeStyleSchema.optional(),
          landscape: safeStyleSchema.optional(),
        })
        .optional(),
      animation: z.union([entranceAnimationSchema, exitAnimationSchema]).optional(),
      animationDurationMs: z.number().int().min(100).max(10_000).optional(),
      animationDelayMs: z.number().int().min(0).max(10_000).optional(),
      animationIteration: z.enum(["once", "infinite"]).optional(),
      textFit: z.enum(["fixed", "shrink"]).optional(),
      textWrap: z.enum(["wrap", "nowrap", "preserve"]).optional(),
      minFontSize: z.number().int().min(6).max(200).optional(),
      ticker: z
        .object({
          direction: z.enum(["left", "right"]),
          speed: z.number().int().min(10).max(500),
          separator: z.string().min(1).max(24),
          separatorColor: z.string().min(1).max(100),
          separatorSpacing: z.number().int().min(0).max(400),
          sequenceGap: z.number().int().min(0).max(1_000),
        })
        .strict()
        .optional(),
      digitalClock: z
        .object({
          showSeconds: z.boolean(),
          use24Hour: z.boolean(),
        })
        .strict()
        .optional(),
      dateFormat: z.string().min(1).max(120).optional(),
      analogClock: z
        .object({
          showHourMarkers: z.boolean(),
          showSecondHand: z.boolean(),
          showNumbers: z.boolean().optional(),
          numberColor: z
            .string()
            .regex(/^#[0-9a-fA-F]{6}$/)
            .optional(),
          numberFontFamily: z.string().min(1).max(200).optional(),
          numberFontSize: z.number().int().min(6).max(80).optional(),
          numberFontWeight: z.number().int().min(100).max(900).optional(),
          numberRadius: z.number().int().min(25).max(44).optional(),
          markerColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
          hourHandColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
          minuteHandColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
          secondHandColor: z.string().regex(/^#[0-9a-fA-F]{6}$/),
          markerLength: z.number().int().min(2).max(20),
          hourHandLength: z.number().int().min(10).max(45),
          minuteHandLength: z.number().int().min(10).max(48),
          secondHandLength: z.number().int().min(10).max(49),
          handThickness: z.number().min(0.5).max(8),
        })
        .strict()
        .optional(),
      carousel: carouselSettingsSchema.optional(),
      positioning: z
        .object({
          mode: z.enum(["free", "center"]),
          horizontalOffset: z.object({
            value: z.number().min(-7680).max(7680),
            unit: z.enum(["%", "px"]),
          }),
          verticalOffset: z.object({
            value: z.number().min(-7680).max(7680),
            unit: z.enum(["%", "px"]),
          }),
        })
        .strict()
        .optional(),
      visibleWhen: z
        .object({
          field: z.string(),
          equals: z.union([z.string(), z.number(), z.boolean()]),
        })
        .optional(),
      children: z.array(layoutNodeSchema).max(100).optional(),
    })
    .strict()
    .superRefine((node, ctx) => {
      if (node.verticalLabelOrientation && node.type !== "verticalLabel")
        ctx.addIssue({
          code: "custom",
          message: "Only vertical labels can set a vertical text orientation",
        });

      if (node.type === "staticImage" && !node.staticImageAssetId)
        ctx.addIssue({
          code: "custom",
          message: "Choose a platform image for the static image widget",
        });
      if (node.staticImageAssetId && node.type !== "staticImage")
        ctx.addIssue({
          code: "custom",
          message: "Only static image widgets can own a platform image",
        });

      const mayHaveChildren = ["container", "stack", "grid", "overlay"].includes(node.type);
      if (!mayHaveChildren && node.children?.length) {
        ctx.addIssue({ code: "custom", message: `${node.type} cannot contain children` });
      }
      if (node.ticker && node.type !== "ticker") {
        ctx.addIssue({ code: "custom", message: `${node.type} cannot use ticker properties` });
      }
      if (node.digitalClock && node.type !== "digitalTime") {
        ctx.addIssue({
          code: "custom",
          message: `${node.type} cannot use digital clock properties`,
        });
      }
      if (node.dateFormat && node.type !== "displayDate") {
        ctx.addIssue({ code: "custom", message: `${node.type} cannot use date format properties` });
      }
      if (node.analogClock && node.type !== "analogClock") {
        ctx.addIssue({
          code: "custom",
          message: `${node.type} cannot use analog clock properties`,
        });
      }
      if (
        node.carousel &&
        !["imageCarousel", "textCarousel", "contentCarousel", "wordpressPosts"].includes(node.type)
      ) {
        ctx.addIssue({
          code: "custom",
          message: `${node.type} cannot use carousel properties`,
        });
      }
      if (
        ["imageCarousel", "textCarousel", "contentCarousel", "wordpressPosts"].includes(
          node.type,
        ) &&
        !node.carousel
      ) {
        ctx.addIssue({ code: "custom", message: `${node.type} requires carousel properties` });
      }
    }),
);

const animationSchema = z.object({
  name: z.union([entranceAnimationSchema, exitAnimationSchema]),
  durationMs: z.number().int().min(0).max(5000),
  delayMs: z.number().int().min(0).max(5000),
});

export const slideTypeDefinitionSchema = z
  .object({
    key: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
    name: z.string().min(1).max(120),
    description: z.string().max(500).default(""),
    version: z.number().int().positive(),
    previewThumbnail: z.string().url().optional(),
    status: z.enum(["draft", "published", "disabled", "archived"]).default("draft"),
    purpose: slidePurposeSchema.default("slide"),
    canvas: z.object({
      width: z.number().int().positive().max(7680),
      height: z.number().int().positive().max(7680),
      aspectRatio: z.string().regex(/^\d+:\d+$/),
      backgroundColor: z.string().default("#000000"),
      safeArea: z.object({
        top: z.number().nonnegative(),
        right: z.number().nonnegative(),
        bottom: z.number().nonnegative(),
        left: z.number().nonnegative(),
      }),
    }),
    fields: z.array(fieldDefinitionSchema).max(80),
    layout: layoutNodeSchema,
    defaultStyles: safeStyleSchema.default({}),
    allowedStyleOverrides: z.array(z.enum(allowedStyleProperties)).default([]),
    animations: z.object({
      entrance: animationSchema,
      exit: animationSchema,
    }),
    availability: z
      .object({
        global: z.boolean(),
        clientIds: z.array(idSchema).default([]),
      })
      .default({ global: true, clientIds: [] }),
  })
  .strict()
  .superRefine((definition, ctx) => {
    if (!definition.availability.global && definition.availability.clientIds.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["availability", "clientIds"],
        message: "Select at least one client or make the slide type available to all clients",
      });
    }
    if (definition.availability.global && definition.availability.clientIds.length > 0) {
      ctx.addIssue({
        code: "custom",
        path: ["availability", "clientIds"],
        message: "Global slide-type availability cannot include individual client assignments",
      });
    }
    if (
      new Set(definition.availability.clientIds).size !== definition.availability.clientIds.length
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["availability", "clientIds"],
        message: "Slide-type client assignments must be unique",
      });
    }
    const fieldKeys = new Set<string>();
    definition.fields.forEach((field, index) => {
      if (fieldKeys.has(field.key)) {
        ctx.addIssue({
          code: "custom",
          path: ["fields", index, "key"],
          message: `Duplicate field key: ${field.key}`,
        });
      }
      if (field.type === "html" && field.defaultValue !== undefined) {
        if (typeof field.defaultValue !== "string") {
          ctx.addIssue({
            code: "custom",
            path: ["fields", index, "defaultValue"],
            message: "Administrator HTML must be text",
          });
        } else {
          try {
            validateSandboxedHtml(field.defaultValue);
          } catch (error) {
            ctx.addIssue({
              code: "custom",
              path: ["fields", index, "defaultValue"],
              message: error instanceof Error ? error.message : "Unsafe HTML content",
            });
          }
        }
      }
      fieldKeys.add(field.key);
      if (field.pattern) {
        try {
          new RegExp(field.pattern);
        } catch {
          ctx.addIssue({
            code: "custom",
            path: ["fields", index, "pattern"],
            message: "Invalid regular expression",
          });
        }
      }
    });
    const wordpressBindings = new Set<string>();
    const collectWordPress = (node: LayoutNode): void => {
      if (node.type === "wordpressPosts" && node.binding) wordpressBindings.add(node.binding);
      node.children?.forEach(collectWordPress);
    };
    collectWordPress(definition.layout);
    const inspect = (node: LayoutNode): void => {
      if (
        node.type === "verticalLabel" &&
        !definition.fields.some(
          (field) =>
            field.key === node.binding &&
            field.type === "text" &&
            field.adminOnly &&
            typeof field.defaultValue === "string",
        )
      )
        ctx.addIssue({
          code: "custom",
          path: ["layout"],
          message: "Vertical labels require administrator-owned label text",
        });

      if (
        node.type === "wordpressPostQr" &&
        (!wordpressBindings.has(node.wordpressSourceBinding ?? "") ||
          !definition.fields.some(
            (field) => field.key === node.wordpressSourceBinding && field.type === "wordpressPosts",
          ))
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["layout"],
          message: "Select an existing WordPress posts widget for the post QR code",
        });
      }

      if (node.binding && !fieldKeys.has(node.binding)) {
        ctx.addIssue({
          code: "custom",
          path: ["layout"],
          message: `Unknown layout binding: ${node.binding}`,
        });
      }
      node.children?.forEach(inspect);
    };
    inspect(definition.layout);
  });
export type SlideTypeDefinition = z.infer<typeof slideTypeDefinitionSchema>;

export function staticImageAssetIds(definition: SlideTypeDefinition): string[] {
  const ids = new Set<string>();
  const visit = (node: LayoutNode): void => {
    if (node.type === "staticImage" && node.staticImageAssetId) ids.add(node.staticImageAssetId);
    node.children?.forEach(visit);
  };
  visit(definition.layout);
  return [...ids];
}

export const googleFontFamilies = [
  "Inter",
  "Roboto",
  "Open Sans",
  "Lato",
  "Montserrat",
  "Poppins",
  "Oswald",
  "Playfair Display",
  "Noto Sans Arabic",
  "Cairo",
  "Tajawal",
] as const;

export const systemFontFamilies = ["Arial", "Tahoma", "Verdana", "Georgia", "Courier New"] as const;

export const supportedFontFamilies = [...googleFontFamilies, ...systemFontFamilies] as const;

export function slideTypeHasVideo(definition: SlideTypeDefinition): boolean {
  const inspect = (node: LayoutNode): boolean =>
    node.type === "video" ||
    node.type === "youtube" ||
    node.type === "youtubeList" ||
    Boolean(node.children?.some((child) => inspect(child)));
  return inspect(definition.layout);
}

const htmlTagPattern = /<[^>]+>/g;
const dangerousRichTextPattern = /<\s*(script|iframe|object|embed|style)|on\w+\s*=|javascript:/i;
const dangerousHtmlPattern =
  /<\s*(script|iframe|frame|frameset|object|embed|form|base|link)\b|<\s*meta\b[^>]*http-equiv\s*=|on\w+\s*=|javascript:|vbscript:|data\s*:\s*text\/html|srcdoc\s*=|@import|expression\s*\(/i;

export function sanitizeRichText(value: string): string {
  if (dangerousRichTextPattern.test(value)) {
    throw new Error("Unsafe rich-text content");
  }
  return value
    .replace(/<(?!\/?(?:p|br|strong|em|u|ul|ol|li)\b)[^>]*>/gi, "")
    .replace(/\s+style\s*=\s*(?:"[^"]*"|'[^']*')/gi, "");
}

export function validateSandboxedHtml(value: string): string {
  if (dangerousHtmlPattern.test(value)) {
    throw new Error("Unsafe HTML content");
  }
  return value;
}

export interface AssetReference {
  id: string;
  clientId: string;
  url: string;
  mimeType: string;
}

export type SlideFieldValues = Record<string, unknown>;

export function slideFieldAssetIds(values: SlideFieldValues): string[] {
  const ids = new Set<string>();
  const inspect = (value: unknown): void => {
    if (Array.isArray(value)) {
      value.forEach(inspect);
      return;
    }
    if (!value || typeof value !== "object") return;
    if (
      "id" in value &&
      "url" in value &&
      "mimeType" in value &&
      typeof value.id === "string" &&
      typeof value.url === "string" &&
      typeof value.mimeType === "string"
    ) {
      ids.add(value.id);
      return;
    }
    Object.values(value).forEach(inspect);
  };
  Object.values(values).forEach(inspect);
  return [...ids];
}

export type VideoPlaybackState = "playing" | "ended" | "error";

function playableAssetUrl(value: unknown): string | undefined {
  if (typeof value === "string") return value;
  if (
    value &&
    typeof value === "object" &&
    "url" in value &&
    typeof (value as { url?: unknown }).url === "string"
  )
    return (value as { url: string }).url;
  return undefined;
}

export function youtubeVideoId(value: unknown): string | undefined {
  const youtubeValue = youtubeUrl(value);
  if (!youtubeValue) return undefined;
  try {
    const url = new URL(youtubeValue);
    if (url.protocol !== "https:") return undefined;
    const hostname = url.hostname.toLowerCase();
    const allowedHostname =
      hostname === "youtu.be" ||
      hostname === "youtube.com" ||
      hostname === "www.youtube.com" ||
      hostname === "m.youtube.com" ||
      hostname === "youtube-nocookie.com" ||
      hostname === "www.youtube-nocookie.com";
    if (!allowedHostname) return undefined;
    const pathSegments = url.pathname.split("/").filter(Boolean);
    const id =
      hostname === "youtu.be"
        ? pathSegments[0]
        : ["embed", "shorts", "live"].includes(pathSegments[0] ?? "")
          ? pathSegments[1]
          : url.searchParams.get("v");
    return id && /^[A-Za-z0-9_-]{6,20}$/.test(id) ? id : undefined;
  } catch {
    return undefined;
  }
}

export function youtubePlaylistId(value: unknown): string | undefined {
  const youtubeValue = youtubeUrl(value);
  if (!youtubeValue) return undefined;
  try {
    const url = new URL(youtubeValue);
    if (url.protocol !== "https:") return undefined;
    if (!["youtube.com", "www.youtube.com", "m.youtube.com"].includes(url.hostname.toLowerCase()))
      return undefined;
    const id = url.searchParams.get("list");
    return id && /^[A-Za-z0-9_-]{10,80}$/.test(id) ? id : undefined;
  } catch {
    return undefined;
  }
}

export function getPlayableVideoNodeIds(
  definition: SlideTypeDefinition,
  values: SlideFieldValues,
): string[] {
  const result: string[] = [];
  const inspect = (node: LayoutNode, path: string): void => {
    if (node.visibleWhen && values[node.visibleWhen.field] !== node.visibleWhen.equals) return;
    const value = node.binding ? values[node.binding] : undefined;
    if (node.type === "video" && playableAssetUrl(value)) result.push(path);
    if (node.type === "youtube" && (youtubeVideoId(value) || youtubePlaylistId(value)))
      result.push(path);
    if (node.type === "youtubeList" && youtubeListValueSchema.safeParse(value).success)
      result.push(path);
    node.children?.forEach((child, index) => inspect(child, `${path}.${index}`));
  };
  inspect(definition.layout, "root");
  return result;
}

export function shouldAdvanceAfterVideoProgress(
  videoIds: string[],
  states: Record<string, VideoPlaybackState>,
  fallbackElapsed: boolean,
): boolean {
  if (!videoIds.length) return false;
  const currentStates = videoIds.map((id) => states[id]);
  const allFinished = currentStates.every((state) => state === "ended" || state === "error");
  const anyPlaying = currentStates.some((state) => state === "playing");
  return allFinished || (fallbackElapsed && !anyPlaying);
}

export function validateSlideFieldValues(
  definition: SlideTypeDefinition,
  input: SlideFieldValues,
): SlideFieldValues {
  const knownFields = new Set(definition.fields.map((field) => field.key));
  for (const key of Object.keys(input)) {
    if (!knownFields.has(key)) throw new Error(`Unknown field: ${key}`);
  }

  const validated: SlideFieldValues = {};
  for (const field of definition.fields) {
    const administratorOwned = field.adminOnly || field.type === "html";
    const value = administratorOwned
      ? field.defaultValue
      : (input[field.key] ?? field.defaultValue);
    if ((value === undefined || value === null || value === "") && field.required) {
      throw new Error(`${field.label} is required`);
    }
    if (value === undefined || value === null || value === "") continue;

    if (
      [
        "text",
        "textarea",
        "richText",
        "html",
        "date",
        "time",
        "datetime",
        "color",
        "url",
        "phone",
      ].includes(field.type)
    ) {
      if (typeof value !== "string") throw new Error(`${field.label} must be text`);
      if (field.minLength !== undefined && value.length < field.minLength)
        throw new Error(`${field.label} is too short`);
      if (field.maxLength !== undefined && value.length > field.maxLength)
        throw new Error(`${field.label} is too long`);
      if (field.pattern && !new RegExp(field.pattern).test(value))
        throw new Error(`${field.label} has an invalid format`);
      if (field.type === "url") {
        const url = new URL(value);
        if (!["http:", "https:"].includes(url.protocol))
          throw new Error(`${field.label} is unsafe`);
      }
      if (field.type === "color" && !brandColorSchema.safeParse(value).success)
        throw new Error(`${field.label} must be a six-digit hex color`);
      if (field.type === "phone" && !/^\+?[0-9][0-9 ()-]{5,24}$/.test(value))
        throw new Error(`${field.label} must be a valid international phone number`);
      validated[field.key] =
        field.type === "richText"
          ? sanitizeRichText(value)
          : field.type === "html"
            ? validateSandboxedHtml(value)
            : value;
      continue;
    }
    if (field.type === "number" || field.type === "fontSize") {
      if (typeof value !== "number" || !Number.isFinite(value))
        throw new Error(`${field.label} must be a number`);
      if (field.min !== undefined && value < field.min)
        throw new Error(`${field.label} is too low`);
      if (field.max !== undefined && value > field.max)
        throw new Error(`${field.label} is too high`);
      validated[field.key] = value;
      continue;
    }
    if (field.type === "boolean") {
      if (typeof value !== "boolean") throw new Error(`${field.label} must be true or false`);
      validated[field.key] = value;
      continue;
    }
    if (field.type === "select") {
      if (typeof value !== "string" && typeof value !== "number" && typeof value !== "boolean")
        throw new Error(`${field.label} must use an available choice`);
      if (
        !field.options?.some(
          (option) => option.value === value || String(option.value) === String(value),
        )
      )
        throw new Error(`${field.label} must use an available choice`);
      validated[field.key] = value;
      continue;
    }
    if (field.type === "multiSelect") {
      if (!Array.isArray(value) || value.some((item) => typeof item !== "string"))
        throw new Error(`${field.label} must be a list`);
      const allowed = new Set(field.options?.map((option) => String(option.value)) ?? []);
      if ((value as string[]).some((item) => !allowed.has(item)))
        throw new Error(`${field.label} contains an unavailable choice`);
      validated[field.key] = value;
      continue;
    }
    if (field.type === "fontFamily") {
      const allowed: readonly string[] =
        field.options?.map((option) => String(option.value)) ?? supportedFontFamilies;
      if (typeof value !== "string" || !allowed.includes(value))
        throw new Error(`${field.label} must use an available font`);
      validated[field.key] = value;
      continue;
    }
    if (field.type === "alignment") {
      if (typeof value !== "string" || !["left", "center", "right"].includes(value))
        throw new Error(`${field.label} must use an available alignment`);
      validated[field.key] = value;
      continue;
    }
    if (field.type === "position") {
      if (
        typeof value !== "string" ||
        ![
          "top-left",
          "top-center",
          "top-right",
          "center",
          "bottom-left",
          "bottom-center",
          "bottom-right",
        ].includes(value)
      )
        throw new Error(`${field.label} must use an available position`);
      validated[field.key] = value;
      continue;
    }
    if (field.type === "rssSource") {
      if (typeof value !== "string" || !idSchema.safeParse(value).success)
        throw new Error(`${field.label} must reference an available RSS source`);
      validated[field.key] = value;
      continue;
    }
    if (field.type === "wordpressPosts") {
      const parsed = wordpressPostsValueSchema.safeParse(value);
      if (!parsed.success)
        throw new Error(
          `${field.label} must use a valid HTTPS wp-json URL, REST post type, and post limit from 1 to 100`,
        );
      // Feed content is resolved by the server, never accepted as client-authored posts.
      validated[field.key] = {
        mode: parsed.data.mode,
        apiUrl: parsed.data.apiUrl,
        postType: parsed.data.postType,
        limit: parsed.data.limit,
      };
      continue;
    }
    if (field.type === "tickerContent") {
      const parsed = tickerContentValueSchema.safeParse(value);
      if (!parsed.success)
        throw new Error(`${field.label} must use manual headlines or an available RSS source`);
      validated[field.key] = parsed.data;
      continue;
    }
    if (field.type === "youtube") {
      const parsed = youtubeValueSchema.safeParse(value);
      if (!parsed.success || (!youtubeVideoId(parsed.data) && !youtubePlaylistId(parsed.data)))
        throw new Error(`${field.label} must use a secure YouTube video or playlist URL`);
      validated[field.key] = parsed.data;
      continue;
    }
    if (field.type === "youtubeList") {
      const parsed = youtubeListValueSchema.safeParse(value);
      if (!parsed.success || parsed.data.urls.some((url) => !youtubeVideoId(url)))
        throw new Error(`${field.label} must contain valid secure YouTube video URLs`);
      validated[field.key] = parsed.data;
      continue;
    }
    if (field.type === "whatsapp") {
      const parsed = z
        .object({
          phone: z.string().regex(/^\+?[0-9][0-9 ()-]{5,24}$/),
          message: z.string().max(1_000).default(""),
        })
        .safeParse(value);
      if (!parsed.success)
        throw new Error(`${field.label} must include a valid phone number and message`);
      validated[field.key] = parsed.data;
      continue;
    }
    if (field.type === "vcard") {
      const parsed = vCardValueSchema.safeParse(value);
      if (!parsed.success)
        throw new Error(
          `${field.label} must include a contact name and valid optional contact details`,
        );
      validated[field.key] = parsed.data;
      continue;
    }
    if (field.type === "imageCarousel") {
      const parsed = imageCarouselValueSchema.safeParse(value);
      if (!parsed.success || (field.required && parsed.data.length === 0))
        throw new Error(`${field.label} must contain locally hosted images`);
      validated[field.key] = parsed.data;
      continue;
    }
    if (field.type === "textCarousel") {
      const parsed = textCarouselValueSchema.safeParse(value);
      if (!parsed.success || (field.required && parsed.data.length === 0))
        throw new Error(`${field.label} must contain title and description items`);
      validated[field.key] = parsed.data;
      continue;
    }
    if (field.type === "contentCarousel") {
      const parsed = contentCarouselValueSchema.safeParse(value);
      if (!parsed.success || (field.required && parsed.data.length === 0))
        throw new Error(`${field.label} must contain image, title, and description items`);
      validated[field.key] = parsed.data;
      continue;
    }
    if (["image", "logo", "video"].includes(field.type)) {
      if (
        value &&
        typeof value === "object" &&
        "id" in value &&
        "url" in value &&
        "mimeType" in value &&
        typeof value.id === "string" &&
        idSchema.safeParse(value.id).success &&
        typeof value.url === "string" &&
        typeof value.mimeType === "string"
      ) {
        if (value.url !== `/api/v1/media/assets/${value.id}`)
          throw new Error(`${field.label} must use a locally hosted asset`);
        if (field.type === "video" && !value.mimeType.startsWith("video/"))
          throw new Error(`${field.label} must use a video asset`);
        if (field.type !== "video" && !value.mimeType.startsWith("image/"))
          throw new Error(`${field.label} must use an image asset`);
        validated[field.key] = value;
        continue;
      }
      throw new Error(`${field.label} must reference a locally hosted asset`);
    }
    validated[field.key] = value;
  }
  return validated;
}

export interface ScheduleWindow {
  validFrom?: Date | null;
  validUntil?: Date | null;
  daysOfWeek?: number[] | null;
  dailyStartTime?: string | null;
  dailyEndTime?: string | null;
}

export function isScheduleActive(schedule: ScheduleWindow, now: Date, timezone: string): boolean {
  if (schedule.validFrom && now < schedule.validFrom) return false;
  if (schedule.validUntil && now >= schedule.validUntil) return false;
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const get = (type: Intl.DateTimeFormatPartTypes): string =>
    parts.find((part) => part.type === type)?.value ?? "";
  const dayLookup: Record<string, number> = {
    Sun: 0,
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
  };
  const localDay = dayLookup[get("weekday")];
  if (
    schedule.daysOfWeek?.length &&
    (localDay === undefined || !schedule.daysOfWeek.includes(localDay))
  )
    return false;
  const localTime = `${get("hour")}:${get("minute")}`;
  if (schedule.dailyStartTime && localTime < schedule.dailyStartTime) return false;
  if (schedule.dailyEndTime && localTime >= schedule.dailyEndTime) return false;
  return true;
}

export function cacheNamespace(
  globalVersion: number,
  clientVersion: number,
  tvVersion: number,
): string {
  return `signage:${globalVersion}:${clientVersion}:${tvVersion}`;
}

export const loginRequestSchema = z.object({
  email: z.string().email().max(254),
  password: z.string().min(8).max(200),
  mode: z.enum(["admin", "client"]),
});

export const accountPasswordSchema = z
  .string()
  .min(12)
  .max(200)
  .regex(/[a-z]/, "Password must include a lowercase letter")
  .regex(/[A-Z]/, "Password must include an uppercase letter")
  .regex(/[0-9]/, "Password must include a number")
  .regex(/[^A-Za-z0-9]/, "Password must include a symbol");

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1).max(200),
    newPassword: accountPasswordSchema,
  })
  .refine((value) => value.currentPassword !== value.newPassword, {
    path: ["newPassword"],
    message: "New password must be different from the current password",
  });

export const changeEmailSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  email: z.string().trim().email().max(254),
});

export const passwordResetRequestSchema = z.object({
  email: z.string().email().max(254),
});

export const passwordResetConfirmSchema = z.object({
  token: z.string().min(40).max(500),
  newPassword: accountPasswordSchema,
});

export const brandColorSchema = z.union([
  z.string().regex(/^#[0-9a-fA-F]{6}$/),
  z.literal("transparent"),
]);
export const brandLogoUrlSchema = z
  .url()
  .max(2_000)
  .refine((value) => value.startsWith("https://") || value.startsWith("http://"), {
    message: "Brand logo URL must use HTTP or HTTPS",
  });
export const playerUrlSchema = z
  .url()
  .max(2_000)
  .refine((value) => value.startsWith("https://") || value.startsWith("http://"), {
    message: "Player URL must use HTTP or HTTPS",
  })
  .refine(
    (value) => {
      const url = new URL(value);
      return !url.username && !url.password;
    },
    { message: "Player URL must not contain credentials" },
  );

export const deploymentEnvironmentSchema = z.enum(["development", "production"]);

export const platformBrandingSchema = z.object({
  name: z.string().min(1).max(120).default("Q-Assist TV"),
  logoUrl: brandLogoUrlSchema.nullable().default(null),
  faviconUrl: brandLogoUrlSchema.nullable().default(null),
  playerUrl: playerUrlSchema.nullable().default(null),
  primaryColor: brandColorSchema.default("#087b82"),
  secondaryColor: brandColorSchema.default("#06656b"),
  environmentMode: deploymentEnvironmentSchema.default("development"),
  clientDashboardEnabled: z.boolean().default(true),
});
export type PlatformBranding = z.infer<typeof platformBrandingSchema>;

export const createClientSchema = z.object({
  name: z.string().min(2).max(120),
  slug: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
  email: z.string().email().max(254),
  password: accountPasswordSchema,
  activeUntil: z.iso.datetime().optional(),
  expiredTvPolicy: expiredTvPolicySchema.default("continue_cached"),
  brandName: z.string().min(1).max(120).optional(),
  brandLogoUrl: brandLogoUrlSchema.optional(),
  brandPrimaryColor: brandColorSchema.default("#087b82"),
  brandSecondaryColor: brandColorSchema.default("#06656b"),
});

const slideInputSchema = z.object({
  name: z.string().trim().min(1).max(160).optional(),
  playlistId: idSchema,
  slideTypeVersionId: idSchema,
  fieldValues: z.record(z.string(), z.unknown()),
  status: z.enum(["draft", "published", "disabled"]).default("draft"),
  defaultDurationMs: z.number().int().min(1000).max(3_600_000).default(10_000),
  advanceOnVideoEnd: z.boolean().default(false),
  videoLoop: z.boolean().optional(),
  videoMuted: z.boolean().default(true),
});

function validateVideoPlaybackOptions(
  value: {
    advanceOnVideoEnd?: boolean | undefined;
    videoLoop?: boolean | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  if (value.advanceOnVideoEnd === true && value.videoLoop === true)
    ctx.addIssue({
      code: "custom",
      path: ["videoLoop"],
      message: "A looping video cannot also advance the slide when playback ends",
    });
}

export const createSlideSchema = slideInputSchema
  .superRefine(validateVideoPlaybackOptions)
  .transform((value) => ({
    ...value,
    videoLoop: value.videoLoop ?? !value.advanceOnVideoEnd,
  }));

export const moveSlideSchema = z.object({
  playlistId: idSchema,
});

export const createPlaylistSchema = z.object({
  name: z.string().min(1).max(160),
  shuffle: z.boolean().default(false),
  overlaySlideId: idSchema.nullable().default(null),
  items: z
    .array(
      z.object({
        slideId: idSchema,
        durationMs: z.number().int().min(1000).max(3_600_000).optional(),
        entranceAnimation: entranceAnimationSchema.optional(),
        exitAnimation: exitAnimationSchema.optional(),
        animationDurationMs: z.number().int().min(100).max(5_000).optional(),
      }),
    )
    .max(500),
});

export const updateSlideSchema = z
  .object({
    name: z.string().min(1).max(160).optional(),
    fieldValues: z.record(z.string(), z.unknown()).optional(),
    status: z.enum(["draft", "published", "disabled"]).optional(),
    defaultDurationMs: z.number().int().min(1000).max(3_600_000).optional(),
    advanceOnVideoEnd: z.boolean().optional(),
    videoLoop: z.boolean().optional(),
    videoMuted: z.boolean().optional(),
  })
  .superRefine(validateVideoPlaybackOptions);
export const updatePlaylistSchema = createPlaylistSchema;

const portableScheduleSchema = z.object({
  validFrom: z.string().datetime().nullable(),
  validUntil: z.string().datetime().nullable(),
  daysOfWeek: z.array(z.number().int().min(0).max(6)).max(7).nullable(),
  dailyStartTime: z.string().nullable(),
  dailyEndTime: z.string().nullable(),
});

const portablePlaylistItemSchema = portableScheduleSchema.extend({
  durationMs: z.number().int().min(1000).max(3_600_000).nullable(),
  entranceAnimation: entranceAnimationSchema.nullable(),
  exitAnimation: exitAnimationSchema.nullable(),
  animationDurationMs: z.number().int().min(100).max(5_000).nullable(),
});

export const portableSlideDataSchema = portableScheduleSchema
  .extend({
    name: z.string().min(1).max(160),
    status: z.enum(["draft", "published", "disabled"]),
    fieldValues: z.record(z.string(), z.unknown()),
    defaultDurationMs: z.number().int().min(1000).max(3_600_000),
    advanceOnVideoEnd: z.boolean(),
    videoLoop: z.boolean(),
    videoMuted: z.boolean(),
    role: slidePurposeSchema,
    slideType: z.object({
      key: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/),
      version: z.number().int().positive(),
      definition: slideTypeDefinitionSchema,
    }),
    playlistItem: portablePlaylistItemSchema.nullable(),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (
      data.slideType.key !== data.slideType.definition.key ||
      data.slideType.version !== data.slideType.definition.version
    )
      ctx.addIssue({
        code: "custom",
        path: ["slideType"],
        message: "The slide-type locator must match its embedded definition",
      });
    if (data.role !== (data.slideType.definition.purpose ?? "slide"))
      ctx.addIssue({
        code: "custom",
        path: ["role"],
        message: "The slide role must match the embedded slide-type purpose",
      });
    if (data.role === "slide" && !data.playlistItem)
      ctx.addIssue({
        code: "custom",
        path: ["playlistItem"],
        message: "A rotating slide export requires playlist item settings",
      });
    if (data.role === "overlay" && data.playlistItem)
      ctx.addIssue({
        code: "custom",
        path: ["playlistItem"],
        message: "An overlay export cannot contain rotating playlist item settings",
      });
  });

export const slideTypeExportSchema = z
  .object({
    format: z.literal("qassist-tv.slide-type"),
    version: z.literal(1),
    exportedAt: z.string().datetime(),
    data: slideTypeDefinitionSchema,
  })
  .strict();

export const slideExportSchema = z
  .object({
    format: z.literal("qassist-tv.slide"),
    version: z.literal(1),
    exportedAt: z.string().datetime(),
    data: portableSlideDataSchema,
  })
  .strict();

export const slideImportSchema = z
  .object({
    playlistId: idSchema,
    document: slideExportSchema,
  })
  .strict();

export const playlistExportSchema = z
  .object({
    format: z.literal("qassist-tv.playlist"),
    version: z.literal(1),
    exportedAt: z.string().datetime(),
    data: z
      .object({
        name: z.string().min(1).max(160),
        status: z.string().min(1).max(40),
        shuffle: z.boolean().default(false),
        futureSchedule: z.record(z.string(), z.unknown()),
        slides: z.array(portableSlideDataSchema).max(1000),
      })
      .strict()
      .superRefine((data, ctx) => {
        if (data.slides.filter((slide) => slide.role === "overlay").length > 1)
          ctx.addIssue({
            code: "custom",
            path: ["slides"],
            message: "A portable playlist can contain at most one selected overlay",
          });
      }),
  })
  .strict();

export const playlistImportSchema = z
  .object({
    document: playlistExportSchema,
  })
  .strict();

export type SlideTypeExport = z.infer<typeof slideTypeExportSchema>;
export type SlideExport = z.infer<typeof slideExportSchema>;
export type PlaylistExport = z.infer<typeof playlistExportSchema>;

export const MAX_AUDIO_TRACKS = 1_000;

export const audioAssetIdsSchema = z
  .array(idSchema)
  .max(MAX_AUDIO_TRACKS, "Audio lists can contain at most 1,000 tracks")
  .refine((ids) => new Set(ids).size === ids.length, "Audio tracks must be unique");

export const audioLibraryInputSchema = z.object({
  name: z.string().trim().min(1).max(120),
  controllerEnabled: z.boolean().default(false),
  audioAssetIds: audioAssetIdsSchema.refine(
    (ids) => ids.length >= 1,
    "Audio lists must contain at least one track",
  ),
});

export const createTvSchema = z.object({
  name: z.string().min(1).max(120),
  timezone: z
    .string()
    .min(1)
    .max(80)
    .refine((value) => {
      try {
        new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
        return true;
      } catch {
        return false;
      }
    }, "Timezone must be a valid IANA timezone")
    .default("Asia/Qatar"),
  orientation: z.enum(["landscape", "portrait"]).default("landscape"),
  resolutionWidth: z.number().int().min(320).max(20_000).default(1920),
  resolutionHeight: z.number().int().min(240).max(20_000).default(1080),
  zoomPercent: z.number().int().min(50).max(200).default(100),
  backgroundColor: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/, "Background color must be a six-digit hex color")
    .default("#000000"),
  backgroundAssetId: idSchema.nullable().default(null),
  activePlaylistId: idSchema.nullable().default(null),
  audioLibraryId: idSchema.nullable().default(null),
  audioAssetIds: audioAssetIdsSchema.default([]),
  audioShuffle: z.boolean().default(false),
  audioMuted: z.boolean().default(false),
  controllerEnabled: z.boolean().default(false),
  audioControllerEnabled: z.boolean().default(false),
});

export const deviceCloudCommandStatusSchema = z.enum([
  "queued",
  "delivered",
  "acknowledged",
  "succeeded",
  "failed",
  "expired",
  "cancelled",
]);
export type DeviceCloudCommandStatus = z.infer<typeof deviceCloudCommandStatusSchema>;

export const deviceCloudCommandTypeSchema = z.enum([
  "read_diagnostics",
  "identify_device",
  "update_settings",
  "update_device_access",
  "connect_wifi",
  "open_wifi_settings",
  "open_android_settings",
  "show_launcher_selection",
  "reload_player",
  "restart_app",
  "check_apk_update",
  "install_tailscale",
  "open_tailscale",
  "authorize_tailscale",
  "set_tailscale_always_on",
  "reboot",
]);
export type DeviceCloudCommandType = z.infer<typeof deviceCloudCommandTypeSchema>;

const cloudHttpUrlSchema = z
  .url()
  .max(2_048)
  .refine((value) => /^https?:\/\//i.test(value), "Use an HTTP(S) URL")
  .refine((value) => !new URL(value).username && !new URL(value).password, {
    message: "URLs must not contain credentials",
  });

export const deviceCloudCommandPayloadSchemas = {
  read_diagnostics: z.object({}).strict(),
  identify_device: z
    .object({
      deviceId: z.string().uuid(),
    })
    .strict(),
  update_settings: z
    .object({
      homeUrl: cloudHttpUrlSchema.optional(),
      updateMetadataUrl: cloudHttpUrlSchema.optional(),
      updateApkUrl: cloudHttpUrlSchema.optional(),
      panelPort: z.number().int().min(1_024).max(65_535).optional(),
      hotspotSsid: z.string().trim().min(1).max(32).optional(),
      hotspotPassword: z.string().min(8).max(63).optional(),
    })
    .strict()
    .refine((value) => Object.keys(value).length > 0, "Choose at least one setting"),
  update_device_access: z
    .object({
      allowAndroidSettings: z.boolean().optional(),
      allowUsbDebugging: z.boolean().optional(),
      allowAppUninstall: z.boolean().optional(),
      allowAppInstall: z.boolean().optional(),
    })
    .strict()
    .refine((value) => Object.keys(value).length > 0, "Choose at least one access setting"),
  connect_wifi: z
    .object({
      ssid: z.string().trim().min(1).max(32),
      password: z.string().max(63),
    })
    .strict(),
  open_wifi_settings: z.object({}).strict(),
  open_android_settings: z.object({}).strict(),
  show_launcher_selection: z.object({}).strict(),
  reload_player: z.object({}).strict(),
  restart_app: z.object({}).strict(),
  check_apk_update: z.object({}).strict(),
  install_tailscale: z.object({}).strict(),
  open_tailscale: z.object({}).strict(),
  authorize_tailscale: z.object({}).strict(),
  set_tailscale_always_on: z
    .object({
      enabled: z.boolean(),
    })
    .strict(),
  reboot: z.object({}).strict(),
} as const;

export const deviceCloudDeviceNameSchema = z
  .object({
    name: z.string().trim().min(1).max(80),
  })
  .strict();

export const deviceCloudCommandRequestSchema = z
  .object({
    type: deviceCloudCommandTypeSchema,
    payload: z.record(z.string(), z.unknown()).default({}),
    idempotencyKey: z.string().trim().min(8).max(160),
    expiresAt: z.string().datetime().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const parsed = deviceCloudCommandPayloadSchemas[value.type].safeParse(value.payload);
    if (!parsed.success)
      for (const issue of parsed.error.issues)
        ctx.addIssue({
          code: "custom",
          path: ["payload", ...issue.path],
          message: issue.message,
        });
  });

export function parseDeviceCloudCommandPayload(
  type: DeviceCloudCommandType,
  payload: unknown,
): Record<string, unknown> {
  return deviceCloudCommandPayloadSchemas[type].parse(payload);
}

export const deviceCloudCommandResultSchema = z
  .object({
    message: z.string().trim().min(1).max(1_000),
    diagnostics: z.record(z.string(), z.unknown()).optional(),
    artifactId: idSchema.optional(),
  })
  .strict();

export const deviceCloudAcknowledgementSchema = z
  .object({
    status: z.enum(["acknowledged", "succeeded", "failed"]),
    result: deviceCloudCommandResultSchema.optional(),
    errorCode: z
      .string()
      .regex(/^[A-Z0-9_]{2,80}$/)
      .optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (value.status === "failed" && !value.errorCode)
      ctx.addIssue({
        code: "custom",
        path: ["errorCode"],
        message: "A failed command requires an error code",
      });
  });

export const deviceCloudEnrollmentCreateSchema = z
  .object({
    installationId: idSchema,
    bootstrapSecret: z.string().min(32).max(256),
    agentVersion: z.string().trim().min(1).max(80),
  })
  .strict();

export const deviceCloudEnrollmentClaimSchema = z
  .object({
    claimCode: z
      .string()
      .trim()
      .regex(/^[A-Z0-9]{8}$/),
  })
  .strict();

export const deviceCloudPendingEnrollmentClaimSchema = z.union([
  z
    .object({
      tvId: idSchema,
      pairingCode: z.never().optional(),
      transfer: z.literal(true).optional(),
    })
    .strict(),
  z
    .object({
      pairingCode: z
        .string()
        .trim()
        .regex(/^\d{6}$/),
      tvId: z.never().optional(),
      transfer: z.literal(true).optional(),
    })
    .strict(),
]);

export const deviceCloudReconnectSchema = z
  .object({
    installationId: idSchema,
    confirmation: z.literal("RECONNECT Q-ASSIST DEVICE"),
  })
  .strict();

export const browserPlayerSessionAssignmentSchema = z
  .object({
    tvId: idSchema,
  })
  .strict();

export const browserSessionWireframeNodeSchema = z
  .object({
    index: z.number().int().min(0).max(159),
    layer: z.enum(["slide", "overlay"]),
    type: z
      .string()
      .trim()
      .min(1)
      .max(40)
      .regex(/^[A-Za-z][A-Za-z0-9]*$/),
    x: z.number().int().min(-32_768).max(32_768),
    y: z.number().int().min(-32_768).max(32_768),
    width: z.number().int().min(0).max(32_768),
    height: z.number().int().min(0).max(32_768),
  })
  .strict();

export const browserSessionWireframeCanvasSchema = z
  .object({
    layer: z.enum(["slide", "overlay"]),
    x: z.number().int().min(-32_768).max(32_768),
    y: z.number().int().min(-32_768).max(32_768),
    width: z.number().int().min(0).max(32_768),
    height: z.number().int().min(0).max(32_768),
  })
  .strict();

export const browserSessionWireframeSchema = z
  .object({
    requestId: idSchema,
    capturedAt: z.string().datetime({ offset: true }),
    viewport: z
      .object({
        width: z.number().int().min(1).max(32_768),
        height: z.number().int().min(1).max(32_768),
      })
      .strict(),
    state: z.enum(["pairing", "loading", "playing", "empty", "disabled", "offline", "error"]),
    slideId: idSchema.nullable(),
    canvases: z.array(browserSessionWireframeCanvasSchema).max(2),
    nodes: z.array(browserSessionWireframeNodeSchema).max(160),
  })
  .strict();

export type BrowserSessionWireframe = z.infer<typeof browserSessionWireframeSchema>;

export const playerCloudHandoffExchangeSchema = z
  .object({
    token: z.string().regex(/^[A-Za-z0-9_-]{43,128}$/),
  })
  .strict();

export const deviceCloudAgentStatusSchema = z
  .object({
    agentVersion: z.string().trim().min(1).max(80),
    apkVersion: z.string().trim().max(80).optional(),
    androidVersion: z.string().trim().max(80).optional(),
    updateStatus: z.string().trim().max(500).optional(),
    diagnostics: z.record(z.string(), z.unknown()).optional(),
  })
  .strict();

export const apiErrorSchema = z.object({
  data: z.null(),
  meta: z.object({ requestId: z.string() }),
  error: z.object({ code: z.string(), message: z.string() }),
});

export function stripMarkup(value: string): string {
  return value.replace(htmlTagPattern, " ").replace(/\s+/g, " ").trim();
}
